import { useEffect, useMemo } from 'react';

import {
  paymentMethodService,
  paymentMethodQuery,
  sessionQuery,
  Reservation,
  getPaymentUserId,
  guestProfileQuery,
  GuestProfile,
  guestProfileService,
} from '@lib/state';
import { useObservable } from './useObservable';
import { useCurrentUserId } from '.';

export function usePaymentMethods(propertyId?: string, corporateAccountId?: string) {
  const isLoggedIn = useObservable(sessionQuery.isLoggedIn, 'async');

  useEffect(() => {
    if (isLoggedIn) {
      paymentMethodService.getPaymentMethods(propertyId, corporateAccountId);
    }
  }, [isLoggedIn, propertyId, corporateAccountId]);

  return useObservable(paymentMethodQuery.availablePaymentMethods);
}

// Returns payment methods based on the guest profile passed in
// This profile may be the delegate of the reservation or the primary contact
export function useChangeBillingPaymentMethods(
  reservation: Reservation,
  bookingGuest?: GuestProfile,
  corporateAccountId?: string
) {
  const paymentMethods = useGuestPaymentMethod(
    bookingGuest?.userId ?? undefined,
    reservation.propertyId,
    corporateAccountId
  );

  return {
    paymentMethods,
    guest: bookingGuest,
  };
}

export function useReservationPaymentMethods(reservation: Reservation) {
  const guest = usePayingGuest(reservation);

  const paymentMethods = useGuestPaymentMethod(
    guest?.userId ?? undefined,
    reservation.propertyId,
    reservation.affiliation?.corporateAccountId ?? undefined
  );

  return {
    paymentMethods,
    guest,
  };
}

export function useGuestPaymentMethod(
  userId?: string,
  propertyId?: string,
  corporateAccountId?: string
) {
  const paymentMethods = useObservable(paymentMethodQuery.paymentMethods);
  const invoiceAccounts = useObservable(paymentMethodQuery.invoiceAccounts);

  useEffect(() => {
    if (userId && propertyId) {
      paymentMethodService.getGuestPaymentMethods(userId, propertyId, corporateAccountId);
    }
  }, [userId, propertyId, corporateAccountId]);

  // Only reset on unmount; intermediate dep changes are safe because:
  // (a) the service cancels in-flight requests and ignores stale responses, and
  // (b) the service clears the store on the latest request's error.
  useEffect(() => {
    return () => paymentMethodService.reset();
  }, []);

  return useMemo(
    () => ({
      creditCards: paymentMethods,
      invoiceAccounts,
    }),
    [paymentMethods, invoiceAccounts]
  );
}

function usePayingGuest(reservation: Reservation): GuestProfile {
  const loggedInUserId = useCurrentUserId();
  const isEmployee = useObservable(sessionQuery.isEmployee);
  const guest = useObservable(guestProfileQuery.guest);

  const contact = useMemo(
    () => reservation.contacts.find(x => x.userId === getPaymentUserId(reservation)),
    [reservation]
  );

  useEffect(() => {
    if (!!loggedInUserId) guestProfileService.getGuestProfile();
  }, [loggedInUserId]);

  // If I am an employee, then I impersonate getPaymentUserId by using their guest info and payment methods.
  // Otherwise, I am the one paying and I use my own payment methods.
  const guestProfile = useMemo(() => {
    const loggedInGuest = guest ?? ({ ...{ userId: loggedInUserId }, sId: '' } as GuestProfile);

    return !!isEmployee ? contact : loggedInGuest;
  }, [contact, loggedInUserId, isEmployee, guest]);

  return useMemo<GuestProfile>(
    () => ({
      userId: guestProfile?.userId ?? '',
      name: guestProfile?.name ?? {},
      contact: guestProfile?.contact ?? {},
      address: guestProfile?.address ?? {},
      sId: guestProfile?.sId ?? '',
    }),
    [guestProfile]
  );
}
